import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../lib/store';

/**
 * The reminder that a guest account is not permanent yet.
 *
 * Everything about it is chosen to stay out of the way. It is a strip at the
 * top of the page, not a modal — nothing has to be closed to keep playing. It
 * says nothing at all for the first hour, because a player who has not hatched
 * their first egg has nothing to protect and being asked to register is exactly
 * the friction this whole feature removes. Dismissing it snoozes it for three
 * days rather than hiding it forever, since the thing it is warning about does
 * not go away.
 *
 * The messages rotate so the fifth week does not read like the first.
 */

const SNOOZE_KEY = 'pokegrove.claim-prompt-until';
const SNOOZE_DAYS = 3;

/** Nothing at all until the account has been played with for this long. */
const QUIET_MINUTES = 60;

const MESSAGES = [
  {
    headline: 'Protect your collection!',
    body: 'Claim your account to keep your Pokémon safe on any device.',
  },
  {
    headline: 'Playing on a new device soon?',
    body: 'Claim your account so you can sign in anywhere.',
  },
  {
    headline: 'This account lives on this device only.',
    body: 'Claiming takes less than a minute and nothing changes in your game.',
  },
];

function snoozedUntil(): number {
  try {
    return Number(window.localStorage.getItem(SNOOZE_KEY) ?? 0);
  } catch {
    return 0;
  }
}

export function ClaimPrompt() {
  const { me } = useSession();
  const [dismissedAt, setDismissedAt] = useState(() => snoozedUntil());

  if (!me?.account.isGuest) return null;

  const startedAt = me.account.guestCreatedAt ? Date.parse(me.account.guestCreatedAt) : Date.now();
  const minutesOld = (Date.now() - startedAt) / 60_000;
  if (minutesOld < QUIET_MINUTES) return null;
  if (dismissedAt > Date.now()) return null;

  // Stable per day rather than per render, so it does not flicker between
  // messages while the player navigates.
  const message = MESSAGES[Math.floor(minutesOld / (60 * 24)) % MESSAGES.length]!;

  function dismiss() {
    const until = Date.now() + SNOOZE_DAYS * 86_400_000;
    try {
      window.localStorage.setItem(SNOOZE_KEY, String(until));
    } catch {
      /* private mode — the strip simply comes back on reload */
    }
    setDismissedAt(until);
  }

  return (
    <div className="claimbar" role="status">
      <span className="claimbar__text">
        <strong>{message.headline}</strong> {message.body}
      </span>
      <Link to="/claim" className="btn btn--gold btn--sm">
        Claim account
      </Link>
      <button type="button" className="claimbar__close" onClick={dismiss} aria-label="Remind me later">
        ×
      </button>
    </div>
  );
}
